<template>
  <div class="psm-page">
    <!-- Loader -->
    <div v-if="loading" class="psm-loader">
      <v-progress-circular :size="64" :width="4" color="#ffb500" indeterminate />
    </div>

    <!-- Not found -->
    <div v-else-if="!productService" class="psm-empty">
      <v-icon size="64" color="#b0bec5">mdi-package-variant-closed-remove</v-icon>
      <h2 class="psm-empty__title">{{ errorTitle }}</h2>
      <p class="psm-empty__text">{{ errorMessage }}</p>
      <v-btn depressed color="#ffb500" class="psm-btn-buy" to="/">Ir al inicio</v-btn>
    </div>

    <template v-else>
      <!-- â”€â”€ Breadcrumb bar â”€â”€ -->
      <div class="psm-topbar">
        <v-container class="psm-topbar__inner">
          <v-avatar size="26" class="mr-1">
            <img v-if="organization && organization.profilePicture" :src="organization.profilePicture" :alt="orgName" />
            <v-icon v-else small color="#1b273e">mdi-domain</v-icon>
          </v-avatar>
          <span class="psm-topbar__org">{{ orgName }}</span>
          <v-icon size="16" color="#b0bec5" class="mx-1">mdi-chevron-right</v-icon>
          <span class="psm-topbar__current">{{ productService.name }}</span>
        </v-container>
      </div>

      <!-- â”€â”€ Main product area â”€â”€ -->
      <v-container class="psm-container">
        <v-row>

          <!-- LEFT â€” Image gallery -->
          <v-col cols="12" md="6" lg="5">
            <div class="psm-gallery">
              <!-- Main image -->
              <div class="psm-gallery__main" @mouseenter="zoomEnter" @mouseleave="zoomLeave">
                <img
                  :src="activeImage"
                  :alt="productService.name"
                  class="psm-gallery__main-img"
                  :class="{ 'psm-gallery__main-img--zoom': zoomed }"
                />
              </div>

              <!-- Thumbnails strip -->
              <div v-if="allImages.length > 1" class="psm-gallery__thumbs">
                <div
                  v-for="(img, i) in allImages"
                  :key="i"
                  class="psm-gallery__thumb"
                  :class="{ 'psm-gallery__thumb--active': activeImageIndex === i }"
                  @click="activeImageIndex = i"
                >
                  <img :src="img" :alt="`imagen ${i + 1}`" />
                </div>
              </div>
            </div>
          </v-col>

          <!-- RIGHT â€” Info + purchase -->
          <v-col cols="12" md="6" lg="7">
            <div class="psm-info">

              <!-- Title -->
              <h1 class="psm-info__title">{{ productService.name }}</h1>
              <p v-if="productService.shortDescription" class="psm-info__short">
                {{ productService.shortDescription }}
              </p>

              <v-divider class="my-4" />

              <!-- Price -->
              <div class="psm-info__price-row">
                <span class="psm-info__price">{{ formattedPrice }}</span>
                <span class="psm-info__currency">{{ productService.currency || "MXN" }}</span>
              </div>

              <!-- Payment methods -->
              <div
                v-if="productService.paymentMethods && productService.paymentMethods.length"
                class="psm-methods mt-3"
              >
                <span class="psm-methods__label">Métodos de pago:</span>
                <span
                  v-for="m in productService.paymentMethods"
                  :key="m"
                  class="psm-methods__chip"
                >{{ paymentMethodLabel(m) }}</span>
              </div>

              <v-divider class="my-4" />

              <!-- Stock / deadline -->
              <div class="psm-info__meta">
                <div v-if="productService.endSaleDate" class="psm-info__meta-row">
                  <v-icon small color="#e53935">mdi-calendar-clock</v-icon>
                  Disponible hasta {{ formatDate(productService.endSaleDate) }}
                </div>
                <div v-if="productService.maxQuantityPerOrder > 1" class="psm-info__meta-row">
                  <v-icon small color="#8c929e">mdi-package-variant</v-icon>
                  Máximo {{ productService.maxQuantityPerOrder }} por orden
                </div>
              </div>

              <!-- Description (inline) -->
              <div v-if="productService.description" class="mt-4">
                <h3 class="psm-inline-desc__title">Descripción del producto</h3>
                <div class="psm-rich-text psm-inline-desc" v-html="productService.description" />
              </div>

              <!-- CTA -->
              <template v-if="canBuy">
                <v-btn
                  block
                  x-large
                  depressed
                  class="psm-btn-buy mt-5"
                  @click="goToCheckout"
                >
                  <v-icon left>mdi-cart-arrow-right</v-icon>
                  Comprar ahora
                </v-btn>
              </template>
              <template v-else>
                <div class="psm-locked mt-4">
                  <v-icon color="#ffb500" small>mdi-lock-outline</v-icon>
                  Este producto está disponible solo para miembros.
                </div>
                <v-btn block rounded outlined class="psm-btn-outline mt-2" :to="{ name: 'login' }">
                  Iniciar sesión
                </v-btn>
              </template>

              <!-- Security note -->
              <div class="psm-secure mt-3">
                <v-icon small color="success">mdi-shield-check</v-icon>
                Pago seguro con encriptación de extremo a extremo.
              </div>
            </div>
          </v-col>
        </v-row>

        <!-- â”€â”€ Bottom: description + details â”€â”€ -->
        <v-row class="mt-8">
          <v-col cols="12" md="8">
            <article v-if="productService.termsAndConditions" class="psm-section mt-6">
              <h3 class="psm-section__title d-flex align-center mb-3">
                <v-icon small class="mr-2" color="#8c929e">mdi-file-document-outline</v-icon>
                Términos y condiciones
              </h3>
              <div class="psm-rich-text" v-html="productService.termsAndConditions" />
            </article>
          </v-col>

          <v-col cols="12" md="4">
            <aside class="psm-details">
              <h3 class="psm-details__title">Detalles</h3>
              <table class="psm-details__table">
                <tr>
                  <td>Moneda</td>
                  <td><strong>{{ productService.currency || "MXN" }}</strong></td>
                </tr>
                <tr v-if="productService.maxQuantityPerOrder">
                  <td>Máx. por orden</td>
                  <td><strong>{{ productService.maxQuantityPerOrder }}</strong></td>
                </tr>
                <tr>
                  <td>Disponibilidad</td>
                  <td>
                    <strong>{{ productService.allowNonMembers ? "Público general" : "Solo miembros" }}</strong>
                  </td>
                </tr>
                <tr v-if="productService.endSaleDate">
                  <td>Disponible hasta</td>
                  <td><strong>{{ formatDate(productService.endSaleDate) }}</strong></td>
                </tr>
              </table>
            </aside>
          </v-col>
        </v-row>
      </v-container>

      <!-- Footer -->
      <footer class="psm-footer">
        <v-container class="text-center">
          <img
            src="@/assets/images/weChamber/poweredby.svg"
            alt="Powered by WeChamber"
            class="psm-footer__logo"
          />
        </v-container>
      </footer>
    </template>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";

const PAYMENT_METHOD_LABELS = {
  CREDIT_CARD: "Tarjeta de crédito/débito",
  CASH: "Efectivo (OXXO)",
  BANK_TRANSFER: "Transferencia SPEI",
  PEIBO: "Pago referenciado (Peibo)",
  MANUALLY_COMPLETED: "Pago manual",
};

const PLACEHOLDER =
  "https://wechamber-public.s3.amazonaws.com/placeholders/product-service-placeholder.png";

export default {
  name: "ProductServiceMicrosite",
  data() {
    return {
      loading: true,
      activeImageIndex: 0,
      zoomed: false,
      errorTitle: "Producto no disponible",
      errorMessage: "El recurso no existe, fue archivado o aún no se publica.",
    };
  },
  computed: {
    ...mapState("productServices", ["publicProductService"]),
    ...mapState("user", ["userData"]),
    productService() {
      return this.publicProductService;
    },
    organization() {
      return this.productService && this.productService.organization;
    },
    orgName() {
      if (!this.organization) return "";
      return (
        this.organization.name ||
        this.organization.firstLastname ||
        "Organización"
      );
    },
    allImages() {
      if (!this.productService) return [PLACEHOLDER];
      const banner = this.productService.bannerImage;
      const gallery = this.productService.gallery || [];
      const imgs = [banner, ...gallery].filter(Boolean);
      return imgs.length ? imgs : [PLACEHOLDER];
    },
    activeImage() {
      return this.allImages[this.activeImageIndex] || PLACEHOLDER;
    },
    formattedPrice() {
      if (!this.productService) return "";
      try {
        return new Intl.NumberFormat("es-MX", {
          style: "currency",
          currency: this.productService.currency || "MXN",
          minimumFractionDigits: 2,
        }).format(Number(this.productService.price || 0));
      } catch (e) {
        return `$${Number(this.productService.price || 0).toFixed(2)}`;
      }
    },
    isAuthenticated() {
      return !!(this.userData && this.userData.token);
    },
    canBuy() {
      if (!this.productService) return false;
      if (this.productService.allowNonMembers) return true;
      return this.isAuthenticated;
    },
  },
  methods: {
    ...mapActions("productServices", [
      "fetchPublicProductService",
      "fetchPublicProductServiceById",
    ]),
    paymentMethodLabel(method) {
      return PAYMENT_METHOD_LABELS[method] || method;
    },
    formatDate(d) {
      try {
        return new Date(d).toLocaleDateString("es-MX", {
          day: "2-digit",
          month: "long",
          year: "numeric",
        });
      } catch (e) {
        return "";
      }
    },
    goToCheckout() {
      if (!this.productService || !this.productService._id) return;
      this.$router.push({
        name: "productServiceCheckout",
        params: { productServiceId: this.productService._id },
      });
    },
    zoomEnter() {
      this.zoomed = true;
    },
    zoomLeave() {
      this.zoomed = false;
    },
  },
  async mounted() {
    const id = this.$route.params.productServiceId;
    const slug = this.$route.params.slug;
    this.loading = true;
    let res;
    if (id) {
      res = await this.fetchPublicProductServiceById(id);
    } else if (slug) {
      res = await this.fetchPublicProductService(slug);
    }
    this.loading = false;
    if (!res.status) {
      this.errorTitle = "Error al cargar";
      this.errorMessage = res.message || "No se pudo cargar el producto. Intenta de nuevo.";
    }
  },
};
</script>

<style scoped>
/* â”€â”€ Base â”€â”€ */
.psm-page {
  background: #ffffff;
  min-height: 100vh;
  font-family: "Inter", "Roboto", system-ui, sans-serif;
  color: #1b273e;
}
.psm-loader,
.psm-empty {
  min-height: 60vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 12px;
  padding: 32px;
}
.psm-empty__title {
  font-size: 22px;
  font-weight: 700;
  color: #1b273e;
  margin: 0;
}
.psm-empty__text {
  color: #8c929e;
  margin: 0;
  max-width: 400px;
}

/* â”€â”€ Topbar (breadcrumb) â”€â”€ */
.psm-topbar {
  background: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
}
.psm-topbar__inner {
  display: flex;
  align-items: center;
  padding-top: 8px !important;
  padding-bottom: 8px !important;
  flex-wrap: wrap;
  gap: 2px;
  font-size: 13px;
}
.psm-topbar__org {
  color: #1976d2;
  font-weight: 500;
  cursor: pointer;
}
.psm-topbar__org:hover {
  text-decoration: underline;
}
.psm-topbar__current {
  color: #555;
  max-width: 260px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* â”€â”€ Container â”€â”€ */
.psm-container {
  padding-top: 28px;
  padding-bottom: 56px;
  max-width: 1200px !important;
}

/* â”€â”€ Gallery â”€â”€ */
.psm-gallery {
  position: sticky;
  top: 16px;
}
.psm-gallery__main {
  width: 100%;
  aspect-ratio: 1 / 1;
  border: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #fafafa;
  cursor: zoom-in;
}
.psm-gallery__main-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 16px;
  transition: transform 0.3s ease;
}
.psm-gallery__main-img--zoom {
  transform: scale(1.06);
}
.psm-gallery__thumbs {
  display: flex;
  gap: 8px;
  margin-top: 10px;
  flex-wrap: wrap;
}
.psm-gallery__thumb {
  width: 64px;
  height: 64px;
  border: 1px solid #e0e0e0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafafa;
  transition: border-color 0.15s;
  flex-shrink: 0;
}
.psm-gallery__thumb img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 4px;
}
.psm-gallery__thumb--active {
  border-color: #e67000;
  border-width: 2px;
}
.psm-gallery__thumb:hover:not(.psm-gallery__thumb--active) {
  border-color: #999;
}

/* â”€â”€ Info panel â”€â”€ */
.psm-info {
  padding-left: 8px;
}
.psm-info__title {
  font-size: 32px;
  font-weight: 600;
  line-height: 1.3;
  color: #1b273e;
  margin: 0 0 8px;
}
.psm-info__short {
  color: #555;
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
}
.psm-inline-desc__title {
  font-size: 16px;
  font-weight: 700;
  color: #1b273e;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin: 0 0 10px;
}
.psm-inline-desc {
  max-height: 260px;
  overflow-y: auto;
}
.psm-info__price-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
}
.psm-info__price {
  font-size: 34px;
  font-weight: 400;
  color: #0f1111;
  line-height: 1;
}
.psm-info__currency {
  font-size: 13px;
  color: #555;
}

/* Payment methods */
.psm-methods {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-size: 13px;
}
.psm-methods__label {
  color: #555;
  font-weight: 500;
}
.psm-methods__chip {
  background: #f0f2f2;
  border: 1px solid #d5d9d9;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 12px;
  color: #1b273e;
}

/* Meta */
.psm-info__meta {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.psm-info__meta-row {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #555;
}

/* Buttons */
.psm-btn-buy.v-btn {
  background: #ffb500 !important;
  color: #1b273e !important;
  font-weight: 700;
  text-transform: none;
  letter-spacing: 0.2px;
  border-radius: 6px !important;
  box-shadow: 0 2px 5px rgba(213, 217, 217, 0.5) !important;
  border: 1px solid #fcd200 !important;
}
.psm-btn-buy.v-btn:hover {
  background: #ffc533 !important;
}
.psm-btn-outline.v-btn {
  border-color: #ffb500 !important;
  color: #1b273e !important;
  font-weight: 700;
  text-transform: none;
  border-radius: 6px !important;
}

/* Locked */
.psm-locked {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff8e6;
  border: 1px solid #ffb500;
  border-radius: 6px;
  padding: 12px 14px;
  font-size: 13.5px;
  color: #1b273e;
}

/* Seller strip */
.psm-seller {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: #fafafa;
}
.psm-seller__info {
  display: flex;
  flex-direction: column;
}
.psm-seller__label {
  font-size: 11px;
  color: #8c929e;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.psm-seller__name {
  font-size: 14px;
  font-weight: 600;
  color: #1b273e;
}

/* Secure note */
.psm-secure {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #555;
}

/* â”€â”€ Bottom sections â”€â”€ */
.psm-section {
  border-top: 3px solid #1b273e;
  padding-top: 16px;
}
.psm-section__title {
  font-size: 18px;
  font-weight: 600;
  color: #1b273e;
  margin: 0 0 4px;
}
.psm-rich-text {
  font-size: 14px;
  line-height: 1.7;
  color: #333;
}
.psm-rich-text :deep(p) {
  margin: 0 0 10px;
}
.psm-rich-text :deep(h1),
.psm-rich-text :deep(h2),
.psm-rich-text :deep(h3) {
  color: #1b273e;
  margin: 16px 0 8px;
}
.psm-rich-text :deep(a) {
  color: #1976d2;
  text-decoration: underline;
}

/* Details table */
.psm-details {
  border: 1px solid #e0e0e0;
  padding: 16px;
}
.psm-details__title {
  font-size: 16px;
  font-weight: 700;
  color: #1b273e;
  margin: 0 0 12px;
}
.psm-details__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.psm-details__table tr {
  border-bottom: 1px solid #f0f0f0;
}
.psm-details__table tr:last-child {
  border-bottom: none;
}
.psm-details__table td {
  padding: 8px 4px;
  vertical-align: top;
}
.psm-details__table td:first-child {
  color: #555;
  width: 50%;
}
.psm-details__table td:last-child {
  color: #0f1111;
}

/* Expansion panel */
.psm-expansion {
  background: transparent !important;
  box-shadow: none !important;
}
.psm-expansion :deep(.v-expansion-panel-header) {
  padding: 0;
  min-height: auto;
}
.psm-expansion :deep(.v-expansion-panel-content__wrap) {
  padding: 12px 0 0;
}

/* â”€â”€ Footer â”€â”€ */
.psm-footer {
  padding: 24px 0 48px;
  border-top: 1px solid #e0e0e0;
  background: #f5f5f5;
}
.psm-footer__logo {
  max-width: 130px;
  opacity: 0.6;
}
</style>
